import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch, faArrowRight, faUpload } from "@fortawesome/free-solid-svg-icons";
import styles from "./YoutubeButton.module.css";

const PROXY_URL =
  process.env.REACT_APP_YT_PROXY_URL || "http://localhost:5174";

const YouTubeIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
    <path d="M23.5 6.2a3.01 3.01 0 0 0-2.12-2.13C19.54 3.5 12 3.5 12 3.5s-7.54 0-9.38.57A3.01 3.01 0 0 0 .5 6.2 31.5 31.5 0 0 0 0 12a31.5 31.5 0 0 0 .5 5.8 3.01 3.01 0 0 0 2.12 2.13C4.46 20.5 12 20.5 12 20.5s7.54 0 9.38-.57a3.01 3.01 0 0 0 2.12-2.13A31.5 31.5 0 0 0 24 12a31.5 31.5 0 0 0-.5-5.8zM9.75 15.5v-7l6.5 3.5-6.5 3.5z" />
  </svg>
);

const YoutubeButton = ({ onFileLoad, onCancel, visible }) => {
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | error
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setStatus("idle");
      setError("");
      setUrl("");
      inputRef.current?.focus();
      // Warm up Railway server while user types
      fetch(`${PROXY_URL}/api/debug`).catch(() => {});
    } else {
      inputRef.current?.blur();
    }
  }, [visible]);

  const handleSubmit = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    setStatus("loading");
    setError("");

    try {
      const res = await fetch(
        `${PROXY_URL}/api/youtube?url=${encodeURIComponent(trimmed)}`
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Server error ${res.status}`);
      }

      const disposition = res.headers.get("Content-Disposition") || "";
      let fileName = "youtube.wav";
      const match = disposition.match(/filename\*?=(?:UTF-8'')?([^;]+)/i);
      if (match) {
        fileName = decodeURIComponent(match[1].replace(/['"]/g, ""));
      }

      const arrayBuffer = await res.arrayBuffer();
      onFileLoad(arrayBuffer, fileName);
    } catch (err) {
      setStatus("error");
      setError(err.message);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
    if (e.key === "Escape") onCancel();
  };

  const isLoading = status === "loading";

  return (
    <div className={styles.ytWrap}>
      <div className={styles.ytLeft}>
        <YouTubeIcon />

        <div className={styles.ytTextWrap}>
          <span className={`${styles.ytTitle} ${status === "error" ? styles.ytTitleError : ""}`}>
            {isLoading ? "Fetching…" : status === "error" ? error : "YouTube URL"}
          </span>
          <div className={styles.ytInputRow}>
            <input
              ref={inputRef}
              type="text"
              className={styles.ytInput}
              placeholder="Paste a URL…"
              value={url}
              onChange={(e) => {
                setUrl(e.target.value);
                if (status === "error") setStatus("idle");
              }}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
            />
            <button
              className={styles.ytSubmit}
              onClick={handleSubmit}
              disabled={isLoading || !url.trim()}
              title="Load from YouTube"
            >
              <FontAwesomeIcon
                icon={isLoading ? faCircleNotch : faArrowRight}
                className={isLoading ? styles.ytSpinner : ""}
              />
            </button>
          </div>
        </div>
      </div>

      <button className={styles.ytCancel} onClick={onCancel} title="Back to file upload">
        <FontAwesomeIcon icon={faUpload} />
      </button>
      {isLoading && <div className={styles.ytLoadingBar} />}
    </div>
  );
};

export default YoutubeButton;
